import {token} from "@atlaskit/tokens";
import {type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {KanbanBoardColumnConfig} from "~/components/ottopilotDataViewer/view";
import {OttopilotEventContainerStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {OttopilotEventContainerTemplateConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTemplateConfiguration";
import {OttopilotEventStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AmaronEnquiryStatusEnum,
    AmaronEventTypes,
    AmaronLeadStatusEnum,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    EmotoradCallStatusEnum,
    EmotoradEventTypes,
    EmotoradEventContainerStatusEnum,
    EmotoradLeadEventStatusEnum,
    EmotoradSalesStatusEnum,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    emotoradLeadEventStatuses,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

const ARCHIVED_KANBAN_FALLBACK_COLUMN_ID = "a0000001-0001-4001-8001-000000000099" as Uuid;
type EventContainerArchiveConfiguration = Pick<
    OttopilotEventContainerTemplateConfiguration,
    "statusColumnConfig"
>;

/** Kanban layout when archived slice shows a single column (3 cards per row in {@link KanbanBoardView}). */
export const ARCHIVED_MODULE_KANBAN_LAYOUT = {
    columnMinWidthPx: 280,
} as const;

function isArchiveNamedKanbanColumn(column: KanbanBoardColumnConfig): boolean {
    const normalized = column.displayName.trim().toLowerCase();
    return normalized.includes("archive") || normalized === "archived";
}

function collectArchiveStatusIdsFromKanbanColumns(
    columnConfig: Array<KanbanBoardColumnConfig>,
    target: Set<string>,
): void {
    for (const column of columnConfig) {
        if (isArchiveNamedKanbanColumn(column) !== true) {
            continue;
        }
        for (const possibleValue of column.possibleValues) {
            target.add(String(possibleValue));
        }
    }
}

export function resolveEventContainerArchivedKanbanStatusIds(
    eventContainerTemplateConfiguration: EventContainerArchiveConfiguration,
    kanbanColumnConfig: Array<KanbanBoardColumnConfig>,
): ReadonlySet<string> {
    const ids = new Set<string>([
        String(OttopilotEventContainerStatus.archived),
        String(EmotoradEventContainerStatusEnum.ARCHIVE),
        String(AmaronLeadStatusEnum.ARCHIVED),
    ]);
    const statusColumnConfig = eventContainerTemplateConfiguration.statusColumnConfig;
    if (statusColumnConfig !== undefined) {
        ids.add(String(statusColumnConfig.archiveStatusValue));
    }
    collectArchiveStatusIdsFromKanbanColumns(
        kanbanColumnConfig,
        ids,
    );
    return ids;
}

export function resolveEventArchivedKanbanStatusIds(eventTemplate: OttopilotEventTemplate): ReadonlySet<string> {
    const ids = new Set<string>([String(OttopilotEventStatus.archived)]);
    const statusColumnConfig = eventTemplate.configuration.statusColumnConfig;
    if (statusColumnConfig !== undefined) {
        ids.add(String(statusColumnConfig.archiveStatusValue));
    }
    if (eventTemplate.id === (EmotoradEventTypes.lead as Uuid)) {
        ids.add(String(emotoradLeadEventStatuses[EmotoradLeadEventStatusEnum.ARCHIVE].id));
    }
    if (eventTemplate.id === (EmotoradEventTypes.call as Uuid)) {
        ids.add(String(EmotoradCallStatusEnum.ARCHIVE));
    }
    if (eventTemplate.id === (EmotoradEventTypes.sales as Uuid)) {
        ids.add(String(EmotoradSalesStatusEnum.ARCHIVE));
    }
    collectArchiveStatusIdsFromKanbanColumns(
        eventTemplate.configuration.eventPage.kanbanBoardPageView.columnConfig,
        ids,
    );
    return ids;
}

/** Active slice hides archive-only columns; archived slice shows only archive columns (single column → 3 cards per row). */
export function filterKanbanColumnsForDataSlice(
    columnConfig: Array<KanbanBoardColumnConfig>,
    slice: ModuleDataSlice,
    archivedStatusIds: ReadonlySet<string>,
): Array<KanbanBoardColumnConfig> {
    if (slice === "archived") {
        const archivedColumns = columnConfig.filter((column) =>
            column.possibleValues.some((value) => archivedStatusIds.has(String(value)) === true),
        );
        if (archivedColumns.length > 0) {
            return archivedColumns;
        }
        return [
            {
                id: ARCHIVED_KANBAN_FALLBACK_COLUMN_ID,
                displayName: "Archived",
                background: token("color.background.accent.red.subtlest"),
                textColor: token("color.text.danger"),
                headerBorderRadius: "4px 4px 0 0",
                headerBorderColor: token("color.border"),
                headerBorderWidth: "0.5px",
                possibleValues: [...archivedStatusIds],
            },
        ];
    }

    return columnConfig.filter((column) =>
        column.possibleValues.every((value) => archivedStatusIds.has(String(value)) === false),
    );
}

export type ModuleDataSlice = "active" | "archived";

export type ModuleSliceRowActions = "full" | "viewOnly";

export type ModuleSliceConfig = {
    slice: ModuleDataSlice;
    pageTitle: string;
    breadcrumbItems: Array<{displayName: string; to: string | null}>;
    mandatoryFilters: FilterConfiguration["lineItems"];
    preservedFilterColumnIds: string[];
    ignoreStatusIdSearchParam: boolean;
    capabilities: {
        showCreate: boolean;
        showExportMenu: boolean;
        showArchivedDataMenuItem: boolean;
        rowActions: ModuleSliceRowActions;
    };
    queryKeySegment: ModuleDataSlice;
};

function buildArchivedEventMandatoryFilters(eventTemplate: OttopilotEventTemplate): FilterConfiguration["lineItems"] {
    const statusColumnConfig = eventTemplate.configuration.statusColumnConfig;

    if (statusColumnConfig !== undefined) {
        return [
            {
                columnId: String(statusColumnConfig.columnId),
                operation: FilterOperation.equals,
                value: statusColumnConfig.archiveStatusValue,
            },
        ];
    }

    if (eventTemplate.id === (EmotoradEventTypes.lead as Uuid)) {
        return [
            {
                columnId: "status",
                operation: FilterOperation.equals,
                value: OttopilotEventStatus.archived,
            },
            {
                columnId: EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
                operation: FilterOperation.equals,
                value: emotoradLeadEventStatuses[EmotoradLeadEventStatusEnum.ARCHIVE].id,
            },
        ];
    }

    return [
        {
            columnId: "status",
            operation: FilterOperation.equals,
            value: OttopilotEventStatus.archived,
        },
    ];
}

function buildActiveEventMandatoryFilters(eventTemplate: OttopilotEventTemplate): FilterConfiguration["lineItems"] {
    const lineItems: FilterConfiguration["lineItems"] = [
        {
            columnId: "eventContainer.status",
            operation: FilterOperation.notEquals,
            value: OttopilotEventContainerStatus.archived,
        },
        {
            columnId: "status",
            operation: FilterOperation.notEquals,
            value: OttopilotEventStatus.archived,
        },
    ];

    const statusColumnConfig = eventTemplate.configuration.statusColumnConfig;
    if (statusColumnConfig !== undefined) {
        lineItems.push({
            columnId: String(statusColumnConfig.columnId),
            operation: FilterOperation.notEquals,
            value: statusColumnConfig.archiveStatusValue,
        });
    }

    if (eventTemplate.id === (EmotoradEventTypes.lead as Uuid)) {
        lineItems.push({
            columnId: EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
            operation: FilterOperation.notEquals,
            value: emotoradLeadEventStatuses[EmotoradLeadEventStatusEnum.ARCHIVE].id,
        });
    }

    if (eventTemplate.id === (AmaronEventTypes.Enquiry as Uuid)) {
        lineItems.push({
            columnId: AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
            operation: FilterOperation.notEquals,
            value: AmaronEnquiryStatusEnum.DUPLICATE,
        });
    }

    return lineItems;
}

function buildActiveEventContainerMandatoryFilters(
    eventContainerTemplateConfiguration: EventContainerArchiveConfiguration,
): FilterConfiguration["lineItems"] {
    const lineItems: FilterConfiguration["lineItems"] = [
        {
            columnId: "status",
            operation: FilterOperation.notEquals,
            value: OttopilotEventContainerStatus.archived,
        },
    ];

    const statusColumnConfig = eventContainerTemplateConfiguration.statusColumnConfig;
    if (statusColumnConfig !== undefined) {
        lineItems.push({
            columnId: String(statusColumnConfig.columnId),
            operation: FilterOperation.notEquals,
            value: statusColumnConfig.archiveStatusValue,
        });
    }

    return lineItems;
}

function buildArchivedEventContainerMandatoryFilters(
    eventContainerTemplateConfiguration: EventContainerArchiveConfiguration,
): FilterConfiguration["lineItems"] {
    const statusColumnConfig = eventContainerTemplateConfiguration.statusColumnConfig;
    const lineItems: FilterConfiguration["lineItems"] = [
        {
            columnId: "status",
            operation: FilterOperation.equals,
            value: OttopilotEventContainerStatus.archived,
        },
    ];

    if (statusColumnConfig !== undefined) {
        lineItems.push({
            columnId: String(statusColumnConfig.columnId),
            operation: FilterOperation.equals,
            value: statusColumnConfig.archiveStatusValue,
        });
    }

    return lineItems;
}

export function buildEventModuleInitialFilterConfiguration(args: {
    slice: ModuleDataSlice;
    eventTemplate: OttopilotEventTemplate;
}): FilterConfiguration {
    const lineItems =
        args.slice === "archived"
            ? buildArchivedEventMandatoryFilters(args.eventTemplate)
            : buildActiveEventMandatoryFilters(args.eventTemplate);

    return {
        revisionId: generateUuid(),
        lineItems: lineItems,
    };
}

function sortedFilterValuesForComparison(values: ReadonlyArray<string>): Array<string> {
    return [...values].map((value) => String(value)).sort();
}

/** True when an existing filter line is superseded by the same mandatory rule (column + operation + value(s)). */
function filterLineItemMatchesMandatoryLineItem(
    lineItem: FilterConfiguration["lineItems"][number],
    mandatoryLineItem: FilterConfiguration["lineItems"][number],
): boolean {
    if (lineItem.columnId !== mandatoryLineItem.columnId) {
        return false;
    }
    if (lineItem.operation !== mandatoryLineItem.operation) {
        return false;
    }
    if (lineItem.operation === FilterOperation.in && mandatoryLineItem.operation === FilterOperation.in) {
        const actual = sortedFilterValuesForComparison(lineItem.values);
        const expected = sortedFilterValuesForComparison(mandatoryLineItem.values);
        return (
            actual.length === expected.length &&
            actual.every((value, index) => value === expected[index])
        );
    }
    if ("value" in lineItem && "value" in mandatoryLineItem) {
        return String(lineItem.value) === String(mandatoryLineItem.value);
    }
    return false;
}

/**
 * Ensures slice mandatory filters (e.g. archived-only) are always sent to the API.
 * Replaces only line items that match the same mandatory rule — not every filter on that column —
 * so sidebar `statusId` equals filters can coexist with mandatory `notEquals` archive on the same column.
 */
export function mergeModuleSliceMandatoryFilters(
    filterConfiguration: FilterConfiguration,
    mandatoryLineItems: FilterConfiguration["lineItems"],
): FilterConfiguration {
    if (mandatoryLineItems.length === 0) {
        return filterConfiguration;
    }
    return {
        ...filterConfiguration,
        lineItems: [
            ...filterConfiguration.lineItems.filter(
                (lineItem) =>
                    mandatoryLineItems.some((mandatoryLineItem) =>
                        filterLineItemMatchesMandatoryLineItem(lineItem, mandatoryLineItem),
                    ) === false,
            ),
            ...mandatoryLineItems,
        ],
    };
}

export function buildEventContainerModuleInitialFilterConfiguration(args: {
    slice: ModuleDataSlice;
    eventContainerTemplateConfiguration: EventContainerArchiveConfiguration;
}): FilterConfiguration {
    const lineItems =
        args.slice === "archived"
            ? buildArchivedEventContainerMandatoryFilters(args.eventContainerTemplateConfiguration)
            : buildActiveEventContainerMandatoryFilters(args.eventContainerTemplateConfiguration);

    return {
        revisionId: generateUuid(),
        lineItems: lineItems,
    };
}

export function resolveEventModuleSliceConfig(args: {
    slice: ModuleDataSlice;
    eventTemplate: OttopilotEventTemplate;
    activePageTitle: string;
    activeRoutePath: string;
}): ModuleSliceConfig {
    const archivedMandatoryFilters = buildArchivedEventMandatoryFilters(args.eventTemplate);
    const preservedFilterColumnIds = archivedMandatoryFilters.map((lineItem) => lineItem.columnId);

    if (args.slice === "archived") {
        return {
            slice: "archived",
            pageTitle: `Archived ${args.activePageTitle}`,
            breadcrumbItems: [
                {displayName: args.activePageTitle, to: args.activeRoutePath},
                {displayName: "Archived Data", to: null},
            ],
            mandatoryFilters: archivedMandatoryFilters,
            preservedFilterColumnIds: preservedFilterColumnIds,
            ignoreStatusIdSearchParam: true,
            capabilities: {
                showCreate: false,
                showExportMenu: true,
                showArchivedDataMenuItem: false,
                rowActions: "viewOnly",
            },
            queryKeySegment: "archived",
        };
    }

    const activeMandatoryFilters = buildActiveEventMandatoryFilters(args.eventTemplate);

    return {
        slice: "active",
        pageTitle: args.activePageTitle,
        breadcrumbItems: [],
        mandatoryFilters: activeMandatoryFilters,
        preservedFilterColumnIds: activeMandatoryFilters.map((lineItem) => lineItem.columnId),
        ignoreStatusIdSearchParam: false,
        capabilities: {
            showCreate: true,
            showExportMenu: true,
            showArchivedDataMenuItem: true,
            rowActions: "full",
        },
        queryKeySegment: "active",
    };
}

export function resolveEventContainerModuleSliceConfig(args: {
    slice: ModuleDataSlice;
    eventContainerTemplateConfiguration: EventContainerArchiveConfiguration;
    activePageTitle: string;
    activeRoutePath: string;
}): ModuleSliceConfig {
    const archivedMandatoryFilters = buildArchivedEventContainerMandatoryFilters(args.eventContainerTemplateConfiguration);
    const preservedFilterColumnIds = archivedMandatoryFilters.map((lineItem) => lineItem.columnId);

    if (args.slice === "archived") {
        return {
            slice: "archived",
            pageTitle: `Archived ${args.activePageTitle}`,
            breadcrumbItems: [
                {displayName: args.activePageTitle, to: args.activeRoutePath},
                {displayName: "Archived Data", to: null},
            ],
            mandatoryFilters: archivedMandatoryFilters,
            preservedFilterColumnIds: preservedFilterColumnIds,
            ignoreStatusIdSearchParam: true,
            capabilities: {
                showCreate: false,
                showExportMenu: true,
                showArchivedDataMenuItem: false,
                rowActions: "viewOnly",
            },
            queryKeySegment: "archived",
        };
    }

    const activeMandatoryFilters = buildActiveEventContainerMandatoryFilters(args.eventContainerTemplateConfiguration);

    return {
        slice: "active",
        pageTitle: args.activePageTitle,
        breadcrumbItems: [],
        mandatoryFilters: activeMandatoryFilters,
        preservedFilterColumnIds: activeMandatoryFilters.map((lineItem) => lineItem.columnId),
        ignoreStatusIdSearchParam: false,
        capabilities: {
            showCreate: true,
            showExportMenu: true,
            showArchivedDataMenuItem: true,
            rowActions: "full",
        },
        queryKeySegment: "active",
    };
}
